import type { ProductCenterProductRow, ProductCenterSort } from "@adpilot/shared";
import { IconArrowDown, IconArrowsSort, IconArrowUp } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { TableCell } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export function SortHeader({
  column,
  label,
  sortBy,
  sortDirection,
  onSortChange,
  align = "start",
}: {
  column: ProductCenterSort;
  label: string;
  sortBy: ProductCenterSort;
  sortDirection: "asc" | "desc";
  onSortChange: (sortBy: ProductCenterSort, direction: "asc" | "desc") => void;
  align?: "start" | "end";
}): React.ReactElement {
  const { t } = useTranslation("productCenter");
  const active = sortBy === column;
  const nextDirection = active && sortDirection === "asc" ? "desc" : "asc";
  const Icon = !active ? IconArrowsSort : sortDirection === "asc" ? IconArrowUp : IconArrowDown;
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-1 rounded-[4px] outline-none hover:text-ink focus-visible:ring-2 focus-visible:ring-ring",
        align === "end" && "justify-end",
      )}
      aria-label={t(nextDirection === "asc" ? "table.sortAscending" : "table.sortDescending", {
        column: label,
      })}
      onClick={() => onSortChange(column, nextDirection)}
    >
      <span>{label}</span>
      <Icon className="size-3" aria-hidden />
    </button>
  );
}

export function AdsMetricCell({
  children,
  status,
}: {
  children: React.ReactNode;
  status: ProductCenterProductRow["ads"]["status"];
}): React.ReactElement {
  const { t } = useTranslation("productCenter");
  if (status === "unavailable") {
    return (
      <TableCell className="text-right" title={t("performance.adsUnavailable")}>
        <span className="font-mono text-muted-foreground">—</span>
      </TableCell>
    );
  }
  return (
    <TableCell
      className={cn(
        "text-right font-mono font-medium tabular-nums text-ink",
        status === "partial" && "text-warning-foreground",
      )}
      title={status === "partial" ? t("performance.adsPartialValue") : undefined}
    >
      {children}
    </TableCell>
  );
}

export function SellerMetricCell({
  value,
  unavailable,
  partial = false,
  unavailableLabel,
}: {
  value: string | null;
  unavailable: boolean;
  partial?: boolean;
  unavailableLabel?: string;
}): React.ReactElement {
  const { t } = useTranslation("productCenter");
  if (!unavailable) {
    return (
      <TableCell
        className={cn(
          "text-right font-mono font-medium tabular-nums text-ink",
          partial && "text-warning-foreground",
        )}
        title={partial ? t("performance.sellerPartialValue") : undefined}
      >
        {value ?? "—"}
      </TableCell>
    );
  }
  return (
    <TableCell className="text-right">
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              className="inline-flex min-w-8 cursor-help justify-end rounded-[4px] font-mono text-muted-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          }
        >
          —
        </TooltipTrigger>
        <TooltipContent>{unavailableLabel ?? t("performance.sellerUnavailable")}</TooltipContent>
      </Tooltip>
    </TableCell>
  );
}
